/**
 * CredentialVaultLoginRequestAnnotation
 *
 * Private vault card for computer-use login asks. Saves to Supabase vault
 * via /api/user-credentials; emits a non-secret chat ack. Solid panel UI,
 * not liquid glass.
 */

'use client'

import { useCallback, useContext, useState } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { ChatbotUIChatContext } from '@/context/chat'
import { emitComputerUseCredentialOutcome } from '@/lib/computer-use/credential-events'
import {
  hostnameFromVaultUrl,
  PrivateVaultLoginForm
} from '@/components/computer-use/private-vault-login-form'
import type { VaultRequestField } from '@/lib/computer-use/vault-request-fields'

// --------------

interface CredentialRequestData {
  crewMemberId?: string
  domain?: string
  fields?: VaultRequestField[]
  reason?: string
  url: string
}

const ANNOTATION_KEYS = [
  'CredentialVault__requestLogin',
  'CredentialVault__requestSecret',
  'computerUse__requestCredential'
] as const

// --------------

function readAnnotationPayload(annotation: Annotation | Annotation2) {
  const record = annotation as Record<string, unknown>
  for (const key of ANNOTATION_KEYS) {
    const raw = record[key] as Record<string, unknown> | undefined
    if (raw) return raw
  }
  return undefined
}

// --------------

export const CredentialVaultLoginRequestAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const [declined, setDeclined] = useState(false)
  const { selectedChat } = useContext(ChatbotUIChatContext)

  const raw = readAnnotationPayload(annotation)
  const data: CredentialRequestData | null = raw
    ? (('result' in raw ? raw.result : raw) as CredentialRequestData)
    : null

  const url = data?.url?.trim() || ''
  const domain = (data?.domain?.trim() || (url ? hostnameFromVaultUrl(url) : '')).toLowerCase()
  const reason = data?.reason?.trim() || `Login for ${domain}`

  const finish = useCallback(
    (status: 'saved' | 'declined') => {
      const chatId = selectedChat?.id || ''
      if (!chatId || !domain) return
      emitComputerUseCredentialOutcome({ chatId, domain, status, url })
    },
    [domain, selectedChat?.id, url]
  )

  if (!raw || !url || !domain) return null
  if (declined) return null

  return (
    <PrivateVaultLoginForm
      crewMemberId={data?.crewMemberId ?? null}
      client="computer_use"
      domain={domain}
      fields={data?.fields ?? null}
      onCancel={() => {
        setDeclined(true)
        finish('declined')
      }}
      onSaved={() => finish('saved')}
      reason={reason}
      sessionId={selectedChat?.id || null}
      url={url}
    />
  )
}
